import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { X, Loader2, ArrowUpFromLine } from "lucide-react";
import { API, authHeaders, useWarehousePriceHints, SOURCE_LABELS } from "./shared";

/**
 * QuickExitModal — one-click warehouse "scarico" (uscita) directly from an
 * inventory row. Client, item, unit and location are pre-filled and locked;
 * the admin only picks a quantity, an optional reference and cost.
 *
 * All exits go through the standard POST /admin/warehouse/movements endpoint,
 * so inventory decrements and monthly invoice cost stay consistent with any
 * other movement created from the "Movimenti" tab.
 */
export default function QuickExitModal({ item, onClose, onSaved }) {
  const max = Number(item?.quantity || 0);
  const [qty, setQty] = useState(max > 0 ? Math.min(1, max) : 1);
  const [reference, setReference] = useState("");
  const [cost, setCost] = useState(0);
  const [note, setNote] = useState("");
  const [exitDate, setExitDate] = useState(new Date().toISOString().slice(0, 10));
  const entryDate = (item?.entry_date || "").slice(0, 10);
  const [saving, setSaving] = useState(false);
  const [costTouched, setCostTouched] = useState(false);
  const hints = useWarehousePriceHints(item?.client_email);
  const unitTariff = Number(hints?.tariff?.out_cost_per_pallet || 0);
  const tariffSource = hints?.sources?.out_cost_per_pallet;
  const suggestedCost = Number((unitTariff * (parseInt(qty, 10) || 0)).toFixed(2));

  useEffect(() => {
    if (!costTouched && suggestedCost > 0) setCost(suggestedCost);
  }, [suggestedCost, costTouched]);

  const submit = async (force = false) => {
    const q = parseInt(qty, 10);
    if (!q || q <= 0) return toast.error("Inserisci una quantità valida");
    setSaving(true);
    try {
      const payload = {
        client_email: item.client_email,
        movement_type: "uscita",
        description: `Scarico rapido: ${item.item_name}`,
        items: [{
          name: item.item_name,
          quantity: q,
          unit: item.unit || "pz",
          description: item.item_description || "",
        }],
        location_code: item.location_code || "",
        reference,
        cost: parseFloat(cost) || 0,
        note,
        entry_date: entryDate || exitDate,
        exit_date: exitDate,
      };
      const url = `${API}/admin/warehouse/movements${force ? "?force=true" : ""}`;
      await axios.post(url, payload, authHeaders());
      toast.success(`Uscita registrata: ${q} ${item.unit || "pz"} di ${item.item_name}`);
      onSaved?.();
      onClose();
    } catch (err) {
      const detail = err?.response?.data?.detail || "Errore registrazione uscita";
      if (err?.response?.status === 400 && detail.includes("Stock insufficiente") && !force) {
        if (window.confirm(`${detail}\n\nProcedere comunque? Lo stock andrà in negativo.`)) {
          setSaving(false);
          return submit(true);
        }
      } else {
        toast.error(detail);
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
      data-testid="quick-exit-modal"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-white rounded-sm shadow-2xl w-full max-w-md">
        <div className="px-5 py-4 border-b border-zinc-200 flex items-center justify-between">
          <div>
            <h3 className="text-lg font-bold text-[#09090B] flex items-center gap-2">
              <ArrowUpFromLine size={16} className="text-red-500" /> Scarico Rapido
            </h3>
            <p className="text-xs text-zinc-500">{item.company || item.client_name} · {item.client_email}</p>
          </div>
          <button onClick={onClose} className="text-zinc-400 hover:text-zinc-700"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-4">
          <div className="bg-zinc-50 border border-zinc-200 rounded-sm p-3">
            <p className="text-[10px] uppercase font-bold tracking-widest text-zinc-500">Articolo</p>
            <p className="text-base font-bold text-[#09090B]">{item.item_name}</p>
            <div className="flex items-center gap-3 text-xs text-zinc-500 mt-1">
              <span>Disponibili: <b className="text-emerald-700">{max} {item.unit || "pz"}</b></span>
              {item.location_code && <span>Pos: <b className="font-mono">{item.location_code}</b></span>}
            </div>
          </div>

          <div>
            <label className="block text-xs font-bold uppercase text-zinc-500 mb-1">Quantità in uscita *</label>
            <div className="flex items-center gap-2">
              <input
                type="number" min="1" max={max || undefined} step="1"
                value={qty} onChange={(e) => setQty(e.target.value)}
                autoFocus
                className="flex-1 h-10 px-3 border border-zinc-300 rounded-sm text-lg font-bold text-center focus:border-blue-500 outline-none"
                data-testid="quick-exit-qty"
              />
              <button
                type="button" onClick={() => setQty(max)}
                className="px-3 h-10 text-xs font-bold uppercase border border-zinc-300 rounded-sm hover:bg-zinc-50"
                title="Scarica tutta la quantità disponibile"
              >
                Max ({max})
              </button>
            </div>
            <p className="text-[11px] text-zinc-400 mt-1">Unità: {item.unit || "pz"}</p>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-bold uppercase text-zinc-500 mb-1">Data uscita merce *</label>
              <input
                type="date" value={exitDate} min={entryDate || undefined} onChange={(e) => setExitDate(e.target.value)}
                className="w-full h-9 px-2 border border-zinc-300 rounded-sm text-sm focus:border-blue-500 outline-none"
                data-testid="quick-exit-date"
              />
              <p className="text-[11px] text-zinc-400 mt-1">
                {entryDate
                  ? `Ingresso: ${entryDate.slice(8,10)}/${entryDate.slice(5,7)}/${entryDate.slice(0,4)} · Giacenza: ${Math.max(0, Math.round((new Date(exitDate) - new Date(entryDate)) / 86400000))} gg`
                  : "Data ingresso non disponibile"}
              </p>
            </div>
            <div>
              <label className="block text-xs font-bold uppercase text-zinc-500 mb-1">Riferimento</label>
              <input
                value={reference} onChange={(e) => setReference(e.target.value)}
                placeholder="DDT-001, ORD-123..."
                className="w-full h-9 px-2 border border-zinc-300 rounded-sm text-sm focus:border-blue-500 outline-none"
              />
            </div>
            <div>
              <label className="block text-xs font-bold uppercase text-zinc-500 mb-1">Costo (€)</label>
              <input
                type="number" step="0.01" min="0"
                value={cost} onChange={(e) => { setCostTouched(true); setCost(e.target.value); }}
                className="w-full h-9 px-2 border border-zinc-300 rounded-sm text-sm focus:border-blue-500 outline-none"
                data-testid="quick-exit-cost"
              />
              {unitTariff > 0 && (
                <p className="text-[10px] text-zinc-500 mt-1" data-testid="quick-exit-cost-hint">
                  {unitTariff.toFixed(2)} €/bancale · <span className="uppercase font-bold text-[#FF5200]">{SOURCE_LABELS[tariffSource] || tariffSource}</span>
                </p>
              )}
            </div>
          </div>

          <div>
            <label className="block text-xs font-bold uppercase text-zinc-500 mb-1">Note</label>
            <textarea
              value={note} onChange={(e) => setNote(e.target.value)}
              placeholder="Note aggiuntive..."
              rows={2}
              className="w-full px-2 py-1.5 border border-zinc-300 rounded-sm text-sm focus:border-blue-500 outline-none resize-none"
            />
          </div>
        </div>
        <div className="px-5 py-3 border-t border-zinc-200 flex justify-end gap-2 bg-zinc-50">
          <button
            onClick={onClose}
            className="border border-zinc-300 bg-white text-zinc-700 px-4 py-2 text-xs font-bold uppercase rounded-sm"
          >
            Annulla
          </button>
          <button
            onClick={() => submit(false)} disabled={saving}
            className="inline-flex items-center gap-2 bg-red-600 text-white px-4 py-2 text-xs font-bold uppercase rounded-sm hover:bg-red-700 disabled:opacity-50"
            data-testid="quick-exit-submit"
          >
            {saving ? <Loader2 size={12} className="animate-spin" /> : <ArrowUpFromLine size={12} />}
            Registra Uscita
          </button>
        </div>
      </div>
    </div>
  );
}
